<template lang="pug">
  main.user-data
    .user-data__header
      img.user-data__avatar(
        :src="avatar"
        :alt="`Аватар пользователя ${fullName}`"
      )
      .user-data__info
        h1.user-data__name {{ fullName }}
        p.user-data__created(v-html="createdDate")

    section.user-data__description
        span.user-data__description-text(v-html="description")
    .user-data__social-links(
      v-if="existSocialLinks.length"
    )
      a.user-data__social-links-item(
        v-for="socialLink of existSocialLinks"
        :key="socialLink.id"
        :href="socialLink.link"
        target="_blank"
        rel="noopener nofollow ugc"
        :class="getClassSocialLink(socialLink)"
        @click="onClickSocialLink(socialLink)"
      )

    .user-data__footer(
      v-if="isOwnProfile"
    )
      a.button.user-data__button-edit(
        :href="`/profile/user/${userId}/edit`"
      )
        |Редактировать профиль
      a.button.user-data__button-settings(
        :href="`/profile/user/${userId}/settings`"
      )
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import ExpandText from '@/components/ExpandText/ExpandText.vue';

  const DEFAULT_AVATAR =
    'data:image/jpeg;base64,R0lGODlhMgAyAMQAAPP1+dzh7eXp8tHY58jR4s3V5O7x9vn6/Oru9NTa6Pz9/uns8/b4+vX2+v7+//b3+trg7MvT5MfQ4vj5+9fd6t7k7uPn8MbO4czT5PHz+NXc6dbc6dLZ6MbP4f///8TN4CH5BAAAAAAALAAAAAAyADIAAAX/YCeOZGmeaKqubJt+cCzPdG3feK7vfF8PFcMk4ivaCIGMZ+kJGJ8xDYBJHUCNmAd164FcexWuGPHVLcToR/kmQLvV61nETffGY206unGPKfViDn0wDIBogx8HhmIdg4qLWxeDE5BbjX1alUyIU5pLiH+aCohnnhODBY+eG32lnh5wcRCvSwt9A7RNfRK5HIO5RH2znraIrwSIH6GLgskBmgbJHxeaCdIfBpCj1x+QVtzLdNwwnYDjiYuX15AY3LyLGtcEmvHJGpoC0nmQfMkKnsHidLCQC0FAKBgQ5KKSwZeRDeUWUjngZIcEC/8k0lnQzsaAbBoXNaAwgwKlkKIqA3wIAQA7';

  export default defineNuxtComponent({
    name: 'UserData',

    components: {
      ExpandText,
    },

    props: {
      fullName: {
        type: String as PropType<string>,
        required: true,
      },
      description: {
        type: String as PropType<string>,
        required: true,
      },
      createdDate: {
        type: String as PropType<string>,
        required: true,
      },
      avatar: {
        type: String as PropType<string>,
        default: DEFAULT_AVATAR,
      },
      isOwnProfile: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
      userId: {
        required: true,
        validator: (prop) => typeof prop === 'number' || prop === null,
      },
      socialLinks: {
        type: Array,
        required: true,
      },
    },

    computed: {
      existSocialLinks(): Array<any> {
        return this.socialLinks.filter(
          (socialLink: any) =>
            socialLink.link && socialLink.name !== 'facebook' && socialLink.name !== 'instagram',
        );
      },
    },

    methods: {
      getClassSocialLink(socialLink: any): any {
        return {
          [`user-data__social-links-item--${socialLink.id}`]: true,
        };
      },

      onClickSocialLink(socialLink: any) {
        this.$emit('click-social-link', socialLink);
      },
    },
  });
</script>

<style lang="scss" scoped>
  $avatarSize: 85px;
  $linkColor: #2962f9;

  .user-data {
    padding: 16px;

    &__header {
      margin-bottom: 16px;

      @include tablet {
        display: flex;
      }

      @include big-tablet {
        display: block;
      }
    }

    &__description-text {
      word-break: break-word;
    }

    &__avatar {
      width: $avatarSize;
      height: $avatarSize;
      border-radius: 50%;
    }

    &__info {
      @include tablet {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        margin-left: 16px;
      }

      @include big-tablet {
        display: block;
        margin-left: 0;
      }
    }

    &__name {
      @include fontH4;
      word-break: break-word;
      margin-bottom: 4px;
    }

    &__social-links {
      margin-top: 20px;

      &-item {
        display: inline-block;
        width: 24px;
        height: 24px;
        position: relative;

        & + & {
          margin-left: 24px;
        }

        &:before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          opacity: 0;
          z-index: 0;
        }

        @include hover {
          &:before {
            background: rgba(0, 0, 0, 0.2);
            opacity: 1;
            z-index: 1;
          }
        }

        &--fb {
          @include fbIcon;

          &:before {
            @include fbIconMask;
          }
        }

        &--vk {
          @include vkIcon;

          &:before {
            @include vkIconMask;
          }
        }

        &--tg {
          @include tgIcon;

          &:before {
            @include tgIconMask;
          }
        }

        &--tw {
          @include twIcon;

          &:before {
            @include twIconMask;
          }
        }

        &--ig {
          @include igIcon;

          &:before {
            @include igIconMask;
          }
        }
      }
    }

    &__description-button {
      @include fontSmallText;
      color: #5c5c5c;

      @include hover {
        cursor: pointer;
      }
    }

    .user-data__footer {
      display: flex;
      justify-content: space-between;
      margin-top: 24px;

      .button {
        border: 1px solid #e7e7e7;
        border-radius: 2px;
        padding: 7px 16px;
        @include fontTextBtn;
        color: #5c5c5c;
        text-align: center;

        @include hover {
          color: white;
          background-color: $linkColor;
          border-color: $linkColor;
        }
      }

      .button + .button {
        margin-left: 8px;
      }
    }

    &__button-edit {
      flex: 1 0 auto;
      text-decoration: none;
    }

    &__button-settings {
      width: 40px;
      height: 40px;
      padding: 0;
      position: relative;
      flex-shrink: 0;

      &:before {
        content: '';
        display: block;
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        mask-image: url('~/assets/images/settings.svg');
        mask-repeat: no-repeat;
        mask-position: center;
        mask-size: 16px;
        background-color: $linkColor;
      }

      @include hover {
        &:before {
          background-color: white;
        }
      }
    }

    &__created {
      @include fontTinyText;
    }
  }
</style>

<style lang="scss">
  .user-data__description a {
    @include linkStyle;
  }

  .user-data__created {
    @include lessThan(1023px) {
      br {
        display: none;
      }
    }

    > time {
      display: inline-block;
    }
  }
</style>
